import { useMemo } from 'react';
import {
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { usesUrlaStyleSections } from '@/config/loanProductRegistry';
import {
  usePatchLoanAgencyFieldsMutation,
  usePatchLoanProductTypeMutation,
} from '@/apis/extended/loanApplicationApi';
import type {
  LoanAgencyFields,
  LoanApplication,
  LoanProductType,
} from '@/types/LoanApplicationType';
import { parseLoanPatchError } from './loanSectionForm';
import { LoanSectionFormFooter } from './LoanSectionFormFooter';
import { useLoanSectionForm } from './useLoanSectionForm';

type Props = {
  application: LoanApplication;
  readOnly?: boolean;
};

const LOAN_PURPOSES = ['Purchase', 'Refinance', 'Other'] as const;
const OCCUPANCY = [
  'Primary residence',
  'Second home',
  'Investment property',
] as const;
const AMORT = ['Fixed rate', 'ARM', 'Other'] as const;

const PROGRAM_OPTIONS: { value: LoanProductType; label: string }[] = [
  { value: 'conventional', label: 'Conventional' },
  { value: 'fha', label: 'FHA' },
  { value: 'va', label: 'VA' },
  { value: 'usda', label: 'USDA' },
];

const emptyAgency = (): LoanAgencyFields => ({
  loanPurpose: '',
  occupancyType: '',
  amortizationType: '',
  loanTermYears: null,
  loanAmount: null,
  estimatedPropertyValue: null,
  monthlyMiEstimate: null,
  vaFundingFeeExempt: false,
  fhaCaseNumber: '',
});

export function AgencyLoanTermsSection({ application, readOnly }: Props) {
  const [patchAgency, { isLoading }] = usePatchLoanAgencyFieldsMutation();
  const [patchProductType, { isLoading: ptLoading }] =
    usePatchLoanProductTypeMutation();

  const initialAgency = useMemo(
    () => ({ ...emptyAgency(), ...(application.agencyFields || {}) }),
    [application._id, application.agencyFields, application.updatedAt]
  );

  const {
    draft: local,
    setDraft: setLocal,
    isDirty,
    markSaved,
    resetDraft,
    readOnly: formReadOnly,
  } = useLoanSectionForm({
    sectionKey: 'agency-loan-terms',
    applicationId: application._id,
    applicationUpdatedAt: application.updatedAt,
    initialValue: initialAgency,
    readOnly,
  });

  const canPickProgram =
    application.flowProfile !== 'investor_dscr' &&
    usesUrlaStyleSections(application.productType, application.flowProfile);

  const saveSection = async () => {
    if (formReadOnly) return;
    try {
      await patchAgency({
        id: application._id,
        ...local,
      }).unwrap();
      markSaved(local);
      toast.success('Saved');
    } catch (e: unknown) {
      toast.error(parseLoanPatchError(e));
    }
  };

  const disabled = formReadOnly || isLoading;

  const onProductTypeChange = async (next: LoanProductType) => {
    if (readOnly) return;
    try {
      await patchProductType({
        id: application._id,
        productType: next,
      }).unwrap();
      toast.success('Loan program updated');
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'data' in e
          ? String((e as { data?: { error?: string } }).data?.error)
          : 'Could not update program';
      toast.error(msg);
    }
  };

  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Loan and property details aligned with URLA Sections I–II (purpose,
        occupancy, loan terms). Complete this for Conventional, FHA, VA, and
        USDA files.
      </Typography>
      <Grid container spacing={2}>
        {canPickProgram && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Loan program"
              value={application.productType}
              disabled={readOnly || ptLoading}
              onChange={(e) =>
                void onProductTypeChange(e.target.value as LoanProductType)
              }
            >
              {PROGRAM_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
              {application.productType === 'other' ? (
                <MenuItem value="other">Other</MenuItem>
              ) : null}
            </TextField>
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            select
            label="Loan purpose"
            value={local.loanPurpose || ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({ ...s, loanPurpose: e.target.value }))
            }
          >
            <MenuItem value="">Select…</MenuItem>
            {LOAN_PURPOSES.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            select
            label="Occupancy"
            value={local.occupancyType || ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({ ...s, occupancyType: e.target.value }))
            }
          >
            <MenuItem value="">Select…</MenuItem>
            {OCCUPANCY.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            select
            label="Amortization type"
            value={local.amortizationType || ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({ ...s, amortizationType: e.target.value }))
            }
          >
            <MenuItem value="">Select…</MenuItem>
            {AMORT.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Loan term (years)"
            type="number"
            value={local.loanTermYears ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                loanTermYears:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Loan amount"
            type="number"
            value={local.loanAmount ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                loanAmount:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Est. property value"
            type="number"
            value={local.estimatedPropertyValue ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                estimatedPropertyValue:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
          />
        </Grid>
        {(application.productType === 'fha' ||
          application.productType === 'conventional') && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Monthly MI estimate (if applicable)"
              type="number"
              value={local.monthlyMiEstimate ?? ''}
              disabled={disabled}
              onChange={(e) =>
                setLocal((s) => ({
                  ...s,
                  monthlyMiEstimate:
                    e.target.value === '' ? null : Number(e.target.value),
                }))
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
          </Grid>
        )}
        {application.productType === 'fha' && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="FHA case # (if assigned)"
              value={local.fhaCaseNumber || ''}
              disabled={disabled}
              onChange={(e) =>
                setLocal((s) => ({ ...s, fhaCaseNumber: e.target.value }))
              }
            />
          </Grid>
        )}
        {application.productType === 'va' && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(local.vaFundingFeeExempt)}
                  disabled={disabled}
                  onChange={(_, v) =>
                    setLocal((s) => ({ ...s, vaFundingFeeExempt: v }))
                  }
                />
              }
              label="VA funding fee exempt (disability / eligible surplus)"
            />
          </Grid>
        )}
      </Grid>
      <LoanSectionFormFooter
        readOnly={formReadOnly}
        isDirty={isDirty}
        isSaving={isLoading}
        saveLabel="Save loan details"
        onSave={() => void saveSection()}
        onCancel={resetDraft}
      />
    </div>
  );
}
